/** Your data: the browser side of backend/chainlit/data_upload_api.py.
 *
 *  A spreadsheet (Excel, CSV, a Google Sheet link) becomes a topic the chat
 *  answers from next to the ERP feeds. The server reads the file, decides the
 *  header, the types and what to leave out, and says each decision in
 *  `notices` with an undo; this file only carries the calls. English only,
 *  like the rest of the product.
 */

export type ColumnType = 'number' | 'date' | 'text';

export interface UploadColumn {
  name: string;
  type: ColumnType;
  empty_pct: number;
}

export interface UploadTopic {
  id: number;
  table_name: string;
  title: string;
  source: 'upload' | 'gsheet';
  kind: string; // xlsx | csv | gsheet
  file_name: string | null;
  sheet_name: string | null;
  sheet_url: string | null;
  header_row: number | null;
  row_count: number;
  columns: UploadColumn[];
  options: UploadOptions;
  grain_text: string | null;
  date_column: string | null;
  /** The person's own sentences about the sheet, as typed on the Describe step. */
  description: string | null;
  update_mode: string;
  created_by_email: string | null;
  created_at: string;
  updated_by_email: string | null;
  updated_at: string;
  last_status: 'ready' | 'failed';
  last_status_note: string | null;
  last_update_date: string | null;
  /** api_config.sandbox_enabled: an admin may take a topic out of the chat. */
  in_chat: boolean;
}

export interface ErpTopic {
  id: string;
  label: string;
  summary: string | null;
  updated_through: string | null;
}

export interface DataPageData {
  erp: ErpTopic[];
  uploads: UploadTopic[];
  limits: {
    max_bytes: number;
    max_rows: number;
    max_columns: number;
    max_topics: number;
    topics: number;
  };
}

export interface UploadOptions {
  sheet?: string | null;
  header_row?: number | null;
  keep_totals?: boolean;
  text_columns?: string[];
  two_row_header?: boolean | null;
}

export interface PreviewColumn {
  name: string;
  type: ColumnType;
  empty_pct: number;
  samples: unknown[];
  min: unknown;
  max: unknown;
  distinct: number | null;
  unparsed: number;
}

export interface Notice {
  id: string;
  kind: 'left_out' | 'changed' | 'kept';
  title: string;
  text: string;
  undo?: UploadOptions;
  undo_label?: string;
}

export interface Preview {
  sheets: string[];
  sheet: string;
  header_row: number;
  columns: PreviewColumn[];
  sample_rows: Record<string, unknown>[];
  row_count: number;
  column_count: number;
  notices: Notice[];
  options: UploadOptions;
  grain: { columns: string[]; text: string } | null;
  date_column: string | null;
}

export interface Staged {
  token: string;
  preview: Preview;
  file_name: string | null;
  kind: string;
}

export interface Draft {
  title: string;
  description: string;
  grain: string;
  date_column: string | null;
  examples: string[];
  /** 'model' when the assistant drafted it; 'columns' is the plain fallback. */
  source: 'model' | 'columns';
}

export interface CommitBody {
  token: string;
  options: UploadOptions;
  title: string;
  description: string;
  grain_text?: string | null;
  date_column?: string | null;
  examples?: string[];
  replace_id?: number | null;
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    const detail =
      typeof err.detail === 'string' ? err.detail : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return res.json();
}

const post = <T>(url: string, body: unknown) =>
  jsonFetch<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

/** Whether this user's company has the page: company users on a migrated
 *  tenant. Admins are refused, which keeps the section out of their rail. */
export const fetchDataStatus = () =>
  jsonFetch<{ available: boolean }>('/api/data/status');

export const fetchDataPage = () => jsonFetch<DataPageData>('/api/data');

export function parseFile(file: File): Promise<Staged> {
  const form = new FormData();
  form.append('file', file, file.name);
  return jsonFetch<Staged>('/api/data/uploads/parse', {
    method: 'POST',
    body: form
  });
}

export const linkSheet = (url: string) =>
  post<Staged>('/api/data/uploads/link', { url });

export const reparse = (token: string, options: UploadOptions) =>
  post<{ preview: Preview }>('/api/data/uploads/reparse', { token, options });

export const describe = (
  token: string,
  options: UploadOptions,
  title: string | null
) => post<Draft>('/api/data/uploads/describe', { token, options, title });

export const commit = (body: CommitBody) =>
  post<{ topic: UploadTopic }>('/api/data/uploads/commit', body);

export const refreshUpload = (id: number) =>
  post<{ topic: UploadTopic }>(`/api/data/uploads/${id}/refresh`, {});

export const deleteUpload = (id: number) =>
  jsonFetch<{ ok: boolean; id: number; title: string }>(
    `/api/data/uploads/${id}`,
    { method: 'DELETE' }
  );

/** An undo from a notice merged into the current options: a forced text
 *  column joins the list, every other key replaces the old value. */
export function applyUndo(
  options: UploadOptions,
  undo: UploadOptions
): UploadOptions {
  const next: UploadOptions = { ...options, ...undo };
  if (undo.text_columns) {
    const merged = new Set([
      ...(options.text_columns ?? []),
      ...undo.text_columns
    ]);
    next.text_columns = Array.from(merged);
  }
  return next;
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return sameDay ? `today ${time}` : formatDay(iso);
}

/** Who did it, from the sign-in email: "belal" rather than the whole address. */
export function whoLabel(email: string | null | undefined): string {
  if (!email) return 'a colleague';
  return email.split('@')[0];
}
